import { createStore } from 'zustand/vanilla';
import { sessionStore } from '@/shared/api';
import { type CompanyScope, GROUP_SCOPE, scopeFromKey, scopeKey } from './company-scope';

const STORAGE_PREFIX = 'jaf:company-scope:';

/** Escopo escolhido pelo usuário da sessão atual (ainda não validado contra as permissões). */
export interface CompanyScopeState {
  userId: string | null;
  scope: CompanyScope;
  select: (scope: CompanyScope) => void;
}

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

/** Lê a preferência salva (só a chave do escopo: não é dado sensível nem credencial). */
function readPreference(userId: string): CompanyScope {
  try {
    return scopeFromKey(window.localStorage.getItem(storageKey(userId)) ?? '') ?? GROUP_SCOPE;
  } catch {
    return GROUP_SCOPE;
  }
}

function writePreference(userId: string, scope: CompanyScope): void {
  try {
    if (scope.kind === 'GROUP') window.localStorage.removeItem(storageKey(userId));
    else window.localStorage.setItem(storageKey(userId), scopeKey(scope));
  } catch {
    // Preferência é conveniência: sem storage, vale só nesta aba.
  }
}

/**
 * Store do escopo do cabeçalho, persistido por usuário entre sessões. Acompanha a sessão: ao
 * trocar de usuário, recarrega a preferência dele; ao sair, volta ao grupo. A API continua
 * filtrando pelas empresas permitidas de qualquer forma.
 */
export function createCompanyScopeStore() {
  const store = createStore<CompanyScopeState>()((set, get) => ({
    userId: null,
    scope: GROUP_SCOPE,
    select: (scope) => {
      const { userId } = get();
      if (userId !== null) writePreference(userId, scope);
      set({ scope });
    },
  }));

  const sync = (userId: string | null): void => {
    if (store.getState().userId === userId) return;
    store.setState({ userId, scope: userId === null ? GROUP_SCOPE : readPreference(userId) });
  };
  sync(sessionStore.getState().session?.userId ?? null);
  sessionStore.subscribe((state) => {
    sync(state.session?.userId ?? null);
  });
  return store;
}

/** Store do escopo da aplicação. */
export const companyScopeStore = createCompanyScopeStore();
